import React, { Suspense, useEffect, useMemo, useState } from "react";
import type { VisualIndicatorSetting, VisualIndicatorWrap } from "../../types/settings-drawer-interface";
import {
  DEFAULT_EMOJI,
  DEFAULT_TOKEN,
  INVALID_REASON_COPY,
  PRESET_EMOJIS,
  TEXT_MAX_LENGTH,
  VISUAL_INDICATOR_WRAPS,
  displayToken,
  toToken,
  validate,
  wrapLabel,
} from "./visualIndicator";

// Loaded only when "More emojis…" opens, so emoji-picker-react stays out of the main bundle.
const EmojiPickerPopover = React.lazy(() => import("./EmojiPickerPopover"));

export const PREVIEW_ITEM_NAMES = ["Website redesign", "Q4 budget review", "Onboarding checklist"];
const DEFAULT_TEXT = "SYNC";

type Mode = "emoji" | "text";

interface Draft {
  mode: Mode;
  emoji: string;
  text: string;
  wrap: VisualIndicatorWrap;
}

const draftFrom = (saved?: VisualIndicatorSetting | null): Draft => {
  const valid = saved ? validate(saved) : null;
  const setting = valid?.ok ? valid.value : null;
  if (setting?.kind === "text") return { mode: "text", emoji: DEFAULT_EMOJI, text: setting.value, wrap: setting.wrap ?? "square" };
  return { mode: "emoji", emoji: setting?.value ?? DEFAULT_EMOJI, text: DEFAULT_TEXT, wrap: "square" };
};

const draftSetting = (draft: Draft): VisualIndicatorSetting =>
  draft.mode === "emoji" ? { kind: "emoji", value: draft.emoji } : { kind: "text", value: draft.text, wrap: draft.wrap };

const S = {
  panel: { background: "#f5f6f8", borderRadius: 8, padding: 14, display: "flex", flexDirection: "column", gap: 12 },
  row: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 },
  muted: { fontSize: 13, color: "#676879" },
  segment: { display: "flex", background: "#ffffff", border: "1px solid #c3c6d4", borderRadius: 6, padding: 2, gap: 2 },
  tiles: { display: "flex", flexWrap: "wrap", gap: 6, position: "relative" },
  more: {
    height: 44,
    padding: "0 14px",
    borderRadius: 6,
    border: "1px dashed #c3c6d4",
    background: "#ffffff",
    fontSize: 13,
    color: "#323338",
    cursor: "pointer",
  },
  popover: {
    position: "absolute",
    top: 50,
    left: 0,
    zIndex: 1300,
    boxShadow: "0 12px 32px rgba(0, 0, 0, 0.25)",
    borderRadius: 8,
    background: "#ffffff",
  },
  input: {
    height: 36,
    width: 140,
    boxSizing: "border-box",
    border: "1px solid #c3c6d4",
    borderRadius: 4,
    padding: "0 10px",
    fontSize: 14,
    fontFamily: "inherit",
    color: "#323338",
  },
  preview: { background: "#ffffff", border: "1px solid #e6e9ef", borderRadius: 6, overflow: "hidden" },
  previewHead: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#676879",
    padding: "6px 12px",
    borderBottom: "1px solid #e6e9ef",
  },
  previewRow: { display: "flex", alignItems: "center", gap: 10, padding: "7px 12px", fontSize: 14, borderBottom: "1px solid #f0f1f5" },
  previewBar: { width: 4, height: 20, borderRadius: 2, background: "#579bfc", display: "block" },
  error: { fontSize: 12, color: "#d83a52", margin: 0 },
  reset: {
    height: 36,
    padding: "0 14px",
    borderRadius: 4,
    border: "1px solid #c3c6d4",
    background: "#ffffff",
    fontSize: 14,
    color: "#323338",
    cursor: "pointer",
  },
} satisfies Record<string, React.CSSProperties>;

const tabStyle = (on: boolean): React.CSSProperties => ({
  height: 30,
  padding: "0 14px",
  borderRadius: 4,
  border: 0,
  fontSize: 13,
  cursor: "pointer",
  ...(on ? { background: "#0073ea", color: "#ffffff", fontWeight: 600 } : { background: "transparent", color: "#323338" }),
});

const tileStyle = (on: boolean): React.CSSProperties => ({
  width: 44,
  height: 44,
  borderRadius: 6,
  fontSize: 20,
  cursor: "pointer",
  background: "#ffffff",
  ...(on ? { border: "2px solid #0073ea", boxShadow: "0 0 0 2px #cce5ff" } : { border: "1px solid #c3c6d4" }),
});

const pillStyle = (on: boolean): React.CSSProperties => ({
  height: 36,
  padding: "0 12px",
  borderRadius: 4,
  fontSize: 14,
  cursor: "pointer",
  ...(on
    ? { background: "#cce5ff", border: "1px solid #0073ea", color: "#003d80", fontWeight: 600 }
    : { background: "#ffffff", border: "1px solid #c3c6d4", color: "#323338" }),
});

const saveStyle = (enabled: boolean): React.CSSProperties => ({
  height: 36,
  padding: "0 16px",
  borderRadius: 4,
  border: 0,
  fontSize: 14,
  fontWeight: 500,
  ...(enabled ? { background: "#0073ea", color: "#ffffff", cursor: "pointer" } : { background: "#e6e9ef", color: "#676879" }),
});

interface VisualIndicatorPickerProps {
  /** The account's saved indicator; missing means ✨. */
  saved?: VisualIndicatorSetting | null;
  onSave: (indicator: VisualIndicatorSetting) => void;
  saving?: boolean;
}

/** Settings › Account › Preferences: choose the emoji or label SIMB puts on synced items' names. */
const VisualIndicatorPicker = ({ saved, onSave, saving = false }: VisualIndicatorPickerProps) => {
  const savedToken = toToken(saved);
  const [draft, setDraft] = useState<Draft>(() => draftFrom(saved));
  const [moreOpen, setMoreOpen] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);

  // A save (or another tab's save) moves what "unchanged" means.
  useEffect(() => {
    setDraft(draftFrom(saved));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedToken]);

  const update = (patch: Partial<Draft>) => setDraft((prev) => ({ ...prev, ...patch }));

  const validation = useMemo(() => validate(draftSetting(draft)), [draft]);
  const draftToken = validation.ok ? toToken(validation.value) : null;
  const dirty = draftToken === null || draftToken !== savedToken;
  const canSave = validation.ok && dirty && !saving;

  const label = draft.text.trim() || DEFAULT_TEXT;
  const shown = draft.mode === "emoji" ? draft.emoji : wrapLabel(label, draft.wrap);
  const current = displayToken(savedToken);
  const status = dirty
    ? `Unsaved change · currently ${current}`
    : savedToken === DEFAULT_TOKEN
      ? `Currently ${current} (default)`
      : `Saved · currently ${current}`;

  const textError = draft.mode === "text" && !validation.ok ? INVALID_REASON_COPY[validation.reason] : null;
  const isPreset = PRESET_EMOJIS.some((p) => p.emoji === draft.emoji);

  const pickFromMore = (emoji: string) => {
    const result = validate({ kind: "emoji", value: emoji });
    if (!result.ok) {
      setMoreError(INVALID_REASON_COPY[result.reason]);
      return;
    }
    setMoreError(null);
    setMoreOpen(false);
    update({ emoji: result.value.value });
  };

  return (
    <div style={S.panel} data-testid="visual-indicator-picker">
      <div style={S.row}>
        <span style={S.muted}>Added to the start of every synced item's name</span>
        <div role="group" aria-label="Indicator type" style={S.segment}>
          <button type="button" aria-pressed={draft.mode === "emoji"} style={tabStyle(draft.mode === "emoji")} onClick={() => update({ mode: "emoji" })}>
            Emoji
          </button>
          <button type="button" aria-pressed={draft.mode === "text"} style={tabStyle(draft.mode === "text")} onClick={() => update({ mode: "text" })}>
            Text
          </button>
        </div>
      </div>

      {draft.mode === "emoji" && (
        <div>
          <div style={S.tiles}>
            {PRESET_EMOJIS.map((p) => (
              <button
                key={p.emoji}
                type="button"
                aria-label={p.label}
                aria-pressed={draft.emoji === p.emoji}
                style={tileStyle(draft.emoji === p.emoji)}
                onClick={() => update({ emoji: p.emoji })}
              >
                {p.emoji}
              </button>
            ))}
            {!isPreset && (
              <button type="button" aria-label={`Chosen emoji ${draft.emoji}`} aria-pressed style={tileStyle(true)}>
                {draft.emoji}
              </button>
            )}
            <button type="button" aria-expanded={moreOpen} style={S.more} onClick={() => setMoreOpen((open) => !open)}>
              More emojis…
            </button>
            {moreOpen && (
              <div style={S.popover} onKeyDown={(e) => e.key === "Escape" && setMoreOpen(false)}>
                <Suspense fallback={<div style={{ padding: 16, fontSize: 13 }}>Loading emojis…</div>}>
                  <EmojiPickerPopover onPick={pickFromMore} />
                </Suspense>
              </div>
            )}
          </div>
          {moreError && (
            <p role="alert" style={{ ...S.error, marginTop: 6 }}>
              {moreError}
            </p>
          )}
        </div>
      )}

      {draft.mode === "text" && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "#676879" }}>
            Label (up to {TEXT_MAX_LENGTH} characters)
            <input
              type="text"
              maxLength={TEXT_MAX_LENGTH}
              value={draft.text}
              aria-invalid={Boolean(textError)}
              onChange={(e) => update({ text: e.target.value })}
              style={S.input}
            />
          </label>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, color: "#676879" }}>Shown as</span>
            <div role="group" aria-label="Shown as" style={{ display: "flex", gap: 4 }}>
              {VISUAL_INDICATOR_WRAPS.map((wrap) => (
                <button
                  key={wrap}
                  type="button"
                  aria-pressed={draft.wrap === wrap}
                  style={pillStyle(draft.wrap === wrap)}
                  onClick={() => update({ wrap })}
                >
                  {wrapLabel(label, wrap)}
                </button>
              ))}
            </div>
          </div>
          {textError && (
            <p role="alert" style={{ ...S.error, flexBasis: "100%" }}>
              {textError}
            </p>
          )}
        </div>
      )}

      <div style={S.preview}>
        <div style={S.previewHead}>Preview on your board</div>
        {PREVIEW_ITEM_NAMES.map((name) => (
          <div key={name} style={S.previewRow} data-testid="visual-indicator-preview-row">
            <span aria-hidden="true" style={S.previewBar} />
            <span>
              <strong style={{ fontWeight: 600 }}>{shown}</strong> {name}
            </span>
          </div>
        ))}
      </div>

      <div style={S.row}>
        <span style={{ fontSize: 12, color: "#676879" }} data-testid="visual-indicator-status">
          {status}
        </span>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" style={S.reset} onClick={() => setDraft((prev) => ({ ...prev, mode: "emoji", emoji: DEFAULT_EMOJI }))}>
            Reset to {DEFAULT_EMOJI}
          </button>
          <button
            type="button"
            disabled={!canSave}
            style={saveStyle(canSave)}
            onClick={() => validation.ok && onSave(validation.value)}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default VisualIndicatorPicker;
